import { LRUMap } from '@frameio/basics/ds/lru';

const CAPACITY = 10_000;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Entity ID -> endpoint, for entities the backend has definitively redirected.
 *
 * This is module-level rather than per-client so that on the server, placement
 * learned while serving one request (or user) is reused by later ones for as
 * long as the process stays up.
 */
const regionForEntity = new LRUMap<string, string>(CAPACITY);

/**
 * Recursively collect every UUID found in a value (e.g. operation variables).
 * Only strings that are entirely a UUID count; variables hold one ID per field.
 */
export function extractEntityIds(
  value: unknown,
  found: Set<string> = new Set()
): Set<string> {
  if (typeof value === 'string') {
    if (UUID_RE.test(value)) {
      found.add(value.toLowerCase());
    }
  } else if (Array.isArray(value)) {
    value.forEach((item) => extractEntityIds(item, found));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => extractEntityIds(item, found));
  }
  return found;
}

/** Endpoint -> number of the given entities assigned to it. */
export function assignedEndpoints(ids: Iterable<string>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const id of ids) {
    const endpoint = regionForEntity.get(id);
    if (endpoint) counts.set(endpoint, (counts.get(endpoint) ?? 0) + 1);
  }
  return counts;
}

export function recordRedirect(ids: Iterable<string>, endpoint: string) {
  for (const id of ids) {
    regionForEntity.set(id, endpoint);
  }
}

export function clearRegionForEntity() {
  regionForEntity.clear();
}
